import { Badge } from "@/components/ui/badge"
import { cn } from "@/lib/utils"
import { zoneTone, RECOMMENDATION_LABEL, TONE_BAR_CLASS, TONE_PILL_CLASS } from "@/lib/gng-score"
import type { GoNoGoAssessment } from "@/stores/strategy-store"

interface GoNoGoStickyStripProps {
  assessment: GoNoGoAssessment
}

/**
 * Compact live-tally band pinned to the top of the Go/No-Go tab while the
 * hero score card is scrolled out of view (sticky sub-header pattern).
 *
 * Positioned absolutely — it renders inside the zero-height sticky slot in
 * GoNoGoSection, so content scrolls under it rather than being pushed down.
 */
export function GoNoGoStickyStrip({ assessment }: GoNoGoStickyStripProps) {
  const scored = assessment.criteria.filter((c) => c.score != null).length
  const total = assessment.criteria.length
  const score = assessment.overallScore ?? 0
  const rec = assessment.recommendation
  const tone = zoneTone(score, assessment.thresholds)

  return (
    <div className="absolute inset-x-0 top-0 -mx-6 px-6 py-2.5 bg-background border-b border-border flex items-center gap-3">
      <span className="text-sm font-medium whitespace-nowrap">Go / No-Go</span>
      <span className="text-sm font-semibold tabular-nums whitespace-nowrap">
        {score}
        <span className="font-normal text-muted-foreground"> / 100</span>
      </span>
      <div className="flex-1 h-1 rounded-full bg-muted overflow-hidden">
        {scored > 0 && (
          <div
            className={cn("h-full rounded-full transition-all duration-300", TONE_BAR_CLASS[tone])}
            style={{ width: `${Math.min(score, 100)}%` }}
          />
        )}
      </div>
      {rec && (
        <Badge className={cn("rounded-full border-transparent", TONE_PILL_CLASS[tone])} dot>
          {RECOMMENDATION_LABEL[rec]}
        </Badge>
      )}
      <span className="text-meta whitespace-nowrap">{scored} of {total} scored</span>
    </div>
  )
}
